// BlurHash 디코더
//
// 앱이 이미지를 내려받는 동안 보여주는 플레이스홀더를 어드민에서도 눈으로 확인하기 위한 용도다.
// 백엔드가 업로드 시 생성한 해시가 실제 이미지와 닮았는지 문자열만 봐서는 알 수 없어서 붙였다.
//
// npm 패키지(blurhash)를 쓰지 않고 표준 알고리즘(https://blurha.sh)을 그대로 옮겨 담았다.
// 이 레포는 rollup 2 + node 16 빌드라 의존성 하나 늘리는 비용이 60줄짜리 순수 함수보다 크다.

const DIGIT_CHARACTERS =
	"0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz#$%*+,-.:;=?@[]^_{|}~";

// base83 디코딩. 규격 외 문자가 섞이면 -1 을 돌려 호출부가 실패로 처리하게 한다.
const decode83 = (str) => {
	let value = 0;
	for (let i = 0; i < str.length; i++) {
		const digit = DIGIT_CHARACTERS.indexOf(str[i]);
		if (digit === -1) return -1;
		value = value * 83 + digit;
	}
	return value;
};

const sRGBToLinear = (value) => {
	const v = value / 255;
	return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};

// 여기서 반올림은 "+0.5 후 trunc" 로 해야 한다.
// Math.round 를 쓰면 이미 더해둔 0.5 에 다시 반올림이 걸려 전 채널이 1 씩 밝아진다.
// (참조 구현 blurhash 2.0.5 와 픽셀 단위로 대조해 확인)
const linearToSRGB = (value) => {
	const v = Math.max(0, Math.min(1, value));
	return v <= 0.0031308
		? Math.trunc(v * 12.92 * 255 + 0.5)
		: Math.trunc((1.055 * Math.pow(v, 1 / 2.4) - 0.055) * 255 + 0.5);
};

const signPow = (value, exponent) => (value < 0 ? -1 : 1) * Math.pow(Math.abs(value), exponent);

const decodeDC = (value) => [
	sRGBToLinear(value >> 16),
	sRGBToLinear((value >> 8) & 255),
	sRGBToLinear(value & 255),
];

const decodeAC = (value, maximumValue) => [
	signPow((Math.floor(value / (19 * 19)) - 9) / 9, 2) * maximumValue,
	signPow(((Math.floor(value / 19) % 19) - 9) / 9, 2) * maximumValue,
	signPow(((value % 19) - 9) / 9, 2) * maximumValue,
];

/**
 * BlurHash 문자열을 RGBA 픽셀로 디코딩한다.
 * @param {string} blurHash - 백엔드가 생성한 해시
 * @param {number} width - 디코딩 폭 (px)
 * @param {number} height - 디코딩 높이 (px)
 * @returns {Uint8ClampedArray|null} RGBA 픽셀. 해시가 규격에 맞지 않으면 null
 */
export const decodeBlurHash = (blurHash, width, height) => {
	if (!blurHash || blurHash.length < 6) return null;

	const sizeFlag = decode83(blurHash[0]);
	if (sizeFlag === -1) return null;
	const numY = Math.floor(sizeFlag / 9) + 1;
	const numX = (sizeFlag % 9) + 1;

	const quantisedMaximumValue = decode83(blurHash[1]);
	if (quantisedMaximumValue === -1) return null;
	const maximumValue = (quantisedMaximumValue + 1) / 166;

	if (blurHash.length !== 4 + 2 * numX * numY) return null;

	const colors = new Array(numX * numY);
	for (let i = 0; i < colors.length; i++) {
		if (i === 0) {
			const value = decode83(blurHash.substring(2, 6));
			if (value === -1) return null;
			colors[0] = decodeDC(value);
		} else {
			const value = decode83(blurHash.substring(4 + i * 2, 6 + i * 2));
			if (value === -1) return null;
			colors[i] = decodeAC(value, maximumValue);
		}
	}

	const pixels = new Uint8ClampedArray(width * height * 4);
	for (let y = 0; y < height; y++) {
		for (let x = 0; x < width; x++) {
			let r = 0;
			let g = 0;
			let b = 0;
			for (let j = 0; j < numY; j++) {
				for (let i = 0; i < numX; i++) {
					const basis = Math.cos((Math.PI * x * i) / width) * Math.cos((Math.PI * y * j) / height);
					const color = colors[i + j * numX];
					r += color[0] * basis;
					g += color[1] * basis;
					b += color[2] * basis;
				}
			}
			const offset = 4 * (x + y * width);
			pixels[offset] = linearToSRGB(r);
			pixels[offset + 1] = linearToSRGB(g);
			pixels[offset + 2] = linearToSRGB(b);
			pixels[offset + 3] = 255;
		}
	}
	return pixels;
};

/**
 * BlurHash 를 <img src> 에 바로 넣을 수 있는 data URL 로 만든다.
 * 저해상도로 디코딩한 뒤 브라우저가 확대하며 부드럽게 보간하도록 두는 것이
 * 원본 알고리즘의 의도이자 실제 앱에서 보이는 모습이다.
 * @param {string} blurHash - 백엔드가 생성한 해시
 * @param {number} [width=32] - 디코딩 폭 (px)
 * @param {number} [height=32] - 디코딩 높이 (px)
 * @returns {string} data URL. 해시가 잘못됐거나 캔버스를 못 쓰면 빈 문자열
 */
export const blurHashToDataUrl = (blurHash, width = 32, height = 32) => {
	const pixels = decodeBlurHash(blurHash, width, height);
	if (!pixels) return "";

	const canvas = document.createElement("canvas");
	canvas.width = width;
	canvas.height = height;
	const context = canvas.getContext("2d");
	if (!context) return "";

	const imageData = context.createImageData(width, height);
	imageData.data.set(pixels);
	context.putImageData(imageData, 0, 0);
	return canvas.toDataURL();
};
